import { useEffect, useRef } from "react";
import { Info, Palette } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { useTheme } from "@/lib/theme/ThemeProvider";
import ThemeEditor from "@/components/appearance/ThemeEditor";
import ThemePreview from "@/components/appearance/ThemePreview";
import AppearanceActions from "@/components/appearance/AppearanceActions";
import { describeTheme } from "@/components/appearance/describe";

function Summary({ eff, presets }) {
  const d = describeTheme(eff, presets);
  return (
    <div className="space-y-2">
      <div className="inline-flex flex-wrap items-center gap-2 rounded-lg border border-le-border bg-le-surface px-3 py-2 text-sm" data-testid="appearance-effective">
        <Palette className="h-4 w-4 text-le-primary-text" aria-hidden />
        <span className="text-le-t-2">Saved theme:</span>
        <span className="font-semibold text-le-text">{d.name}</span>
        <span className="text-le-t-2">· {d.source}</span>
      </div>
      {eff.policy_blocked && (
        <p className="flex items-start gap-2 text-sm text-amber-900" data-testid="appearance-policy-blocked">
          <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          The clinic owner has turned off the theme you saved, so you&apos;re seeing the clinic default. Your choice is kept in case it&apos;s allowed again.
        </p>
      )}
    </div>
  );
}

export default function Appearance() {
  const th = useTheme();
  const dirtyRef = useRef(false);
  dirtyRef.current = th.dirty && th.draft?.kind === "personal";
  useEffect(() => () => {
    if (dirtyRef.current) toast.info("Your theme preview is still on", { description: "Save or cancel it from the bar at the bottom of the screen." });
  }, []);

  if (!th.eff) return <div className="text-sm text-le-t-2" data-testid="appearance-loading">Loading appearance…</div>;
  const vars = th.preview?.vars || th.eff.vars;

  return (
    <div className="space-y-8" data-testid="appearance-page">
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">Personal settings</div>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl font-bold tracking-tight text-zinc-950">Appearance</h1>
        <p className="mt-2 max-w-2xl text-sm text-zinc-600">
          Choose how Living Energy Hub looks for you. Changes preview across the app straight away and only apply to your account once you save. Your records, bookings and reports are never affected.
        </p>
      </div>
      <Summary eff={th.eff} presets={th.presets} />
      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,420px)]">
        <Card className="overflow-hidden rounded-xl border border-le-border shadow-sm">
          <div className="p-6">
            <ThemeEditor settings={th.settings} onChange={th.updateDraft} eff={th.eff} presets={th.presets} issues={th.preview?.issues || []} />
          </div>
          <AppearanceActions />
        </Card>
        <div className="space-y-2 lg:sticky lg:top-24">
          <div className="text-sm font-semibold text-le-text">Live preview</div>
          <ThemePreview vars={vars} />
          <p className="text-xs text-le-t-2">Example content only — not real appointments.</p>
        </div>
      </div>
    </div>
  );
}
